import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import posthog from 'posthog-js';
import { environment } from '../../../../../../environments/environment';
import { PostHogService } from './posthog.service';
import { StorageService } from './storage.service';
import {
  OptimisticMessage,
  PollMode,
  SupportMessage,
  SupportTicket,
} from './support-chat.types';

interface ConversationsIdentity {
  distinctId: string;
  hash: string;
}

const NOTIFIED_KEY = 'support-chat:notified';
const DRAFT_PREFIX = 'ui.chat.draft.';

/**
 * Facade over posthog.conversations for the in-app support chat.
 *
 * Owns: the adaptive polling engine (thread 5s / list 15s / background 60s /
 * paused on hidden tab), unread state, toast-dedup, and per-ticket drafts.
 * Identity access goes through PostHogService (centralised); this service
 * only touches posthog.conversations.* directly — it is their designated owner.
 */
@Injectable({ providedIn: 'root' })
export class SupportChatService {
  private readonly http = inject(HttpClient);
  private readonly postHog = inject(PostHogService);
  private readonly storage = inject(StorageService);

  // ---- state ----
  private readonly _available = signal(false);
  private readonly _tickets = signal<SupportTicket[]>([]);
  private readonly _activeTicketId = signal<string | null>(null);
  private readonly _messages = signal<OptimisticMessage[]>([]);
  private readonly _pollMode = signal<PollMode>('paused');
  private readonly _sending = signal(false);
  private readonly _loadingThread = signal(false);
  private readonly _loadingList = signal(false);

  readonly available = this._available.asReadonly();
  readonly tickets = this._tickets.asReadonly();
  readonly activeTicketId = this._activeTicketId.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly pollMode = this._pollMode.asReadonly();
  readonly sending = this._sending.asReadonly();
  readonly loadingThread = this._loadingThread.asReadonly();
  readonly loadingList = this._loadingList.asReadonly();

  readonly unreadTotal = computed(() =>
    this._tickets().reduce((sum, t) => sum + (t.unread_count ?? 0), 0),
  );

  readonly newInboundMessage$ = new Subject<SupportMessage>();

  private forceNewTicket = false;
  private panelOpen = false;
  private notifiedMessageIds = this.readNotifiedIds();
  private availabilityTimer: ReturnType<typeof setInterval> | null = null;

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollInFlight = false;
  private consecutiveFailures = 0;
  private lastMessageIso: string | null = null;

  /**
   * Whether a logged-in user currently wants the chat lifecycle running.
   * Independent of `available` (has posthog.conversations loaded). Polling only
   * runs when BOTH are true. Set by verifyIdentity(), cleared by clearIdentity().
   */
  private lifecycleActive = false;

  constructor() {
    this.startAvailabilityWatch();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.clearPollTimer(); // pause
          return;
        }
        // Back to visible: catch up soon, but go through the same single-flight
        // pollOnce() as the scheduled timer (a direct pollOnce() here raced the
        // timer and double-fired the new-message toast).
        this.clearPollTimer();
        if (this.intervalFor(this._pollMode()) != null) {
          this.pollTimer = setTimeout(() => void this.pollOnce(), 500);
        }
      });
    }
  }

  // ---- lifecycle ----

  /**
   * Poll posthog.conversations until it reports available. Safe to call
   * repeatedly — a running timer is not duplicated. Does NOT touch identity
   * or poll mode; it only flips `available` and re-evaluates polling.
   */
  private startAvailabilityWatch(): void {
    if (this.availabilityTimer) return;
    if (this._available()) return;

    const check = () => {
      if (!posthog.conversations?.isAvailable?.()) return;
      this._available.set(true);
      if (this.availabilityTimer) {
        clearInterval(this.availabilityTimer);
        this.availabilityTimer = null;
      }
      this.evaluatePolling();
    };

    check();
    if (!this._available()) {
      this.availabilityTimer = setInterval(check, 2000);
    }
  }

  /**
   * Single source of truth for whether polling should be running and in which
   * mode. Called whenever `available`, `lifecycleActive`, `panelOpen`, or the
   * active ticket changes.
   */
  private evaluatePolling(): void {
    if (!this._available() || !this.lifecycleActive) {
      this._pollMode.set('paused');
      this.clearPollTimer();
      return;
    }
    const mode: PollMode = this.panelOpen
      ? this._activeTicketId()
        ? 'thread'
        : 'list'
      : 'background';
    this._pollMode.set(mode);
    this.rearmPolling();
  }

  private get conv() {
    return posthog.conversations ?? null;
  }

  // ---- identity ----

  /**
   * (Re)start the support-chat lifecycle for the current user. Called by
   * AuthService after login AND after a valid session on startup — idempotent
   * and safe to call repeatedly. This is the ONLY thing that resumes polling
   * after clearIdentity() tore it down on a prior logout; the service is
   * providedIn:'root' and never re-instantiated between a logout and a login
   * within the same page load.
   */
  async verifyIdentity(): Promise<void> {
    this.lifecycleActive = true;
    if (posthog.conversations?.isAvailable?.()) this._available.set(true);
    if (!this._available()) this.startAvailabilityWatch();
    try {
      // A failure here is non-fatal: chat degrades to anonymous-session mode.
      // Not routed through ErrorService (which would redirect on 401).
      const { distinctId, hash } = await firstValueFrom(
        this.http.get<ConversationsIdentity>(
          `${environment.API_URL}/users/conversations-identity`,
          { withCredentials: true },
        ),
      );
      this.postHog.setVerifiedIdentity(distinctId, hash);
    } catch {
      // non-fatal: anonymous-session tickets only
    } finally {
      // Resume polling regardless of whether the hash fetch succeeded —
      // anonymous-session tickets still work and still need polling.
      this.evaluatePolling();
    }
  }

  clearIdentity(): void {
    this.lifecycleActive = false;
    this.postHog.clearVerifiedIdentity();
    this._tickets.set([]);
    this._messages.set([]);
    this._activeTicketId.set(null);
    this._loadingList.set(false);
    this._loadingThread.set(false);
    this.lastMessageIso = null;
    this.notifiedMessageIds = new Set();
    this.writeNotifiedIds();
    this.evaluatePolling(); // → paused
  }

  // ---- tickets / messages ----

  private refreshInFlight: Promise<void> | null = null;

  /**
   * Single-flight: concurrent callers (poll timer, panel ngOnInit, sendMessage,
   * visibility change) share one in-flight request rather than each firing their
   * own getTickets() — which was double-running detectNewInbound() and firing
   * the new-message toast twice.
   */
  async refreshTickets(): Promise<void> {
    if (this.refreshInFlight) return this.refreshInFlight;
    if (!this.conv?.isAvailable?.()) return;
    this.refreshInFlight = this.doRefreshTickets().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefreshTickets(): Promise<void> {
    const conv = this.conv;
    if (!conv) return;
    if (this._tickets().length === 0) this._loadingList.set(true);
    try {
      const res = await conv.getTickets({ limit: 50 });
      const tickets: SupportTicket[] = (res?.results ?? []) as SupportTicket[];
      this.detectNewInbound(tickets);
      this._tickets.set(tickets);
    } catch {
      /* swallow — polling engine handles backoff */
    } finally {
      this._loadingList.set(false);
    }
  }

  async openTicket(id: string): Promise<void> {
    if (!this.conv) return;
    const switching = this._activeTicketId() !== id;
    this._activeTicketId.set(id);
    this.evaluatePolling(); // → thread
    // Clear stale messages when switching threads so the view doesn't briefly
    // show the previous conversation, and so the messages() signal makes a
    // clean [] → [loaded] transition (the panel's scroll-to-bottom effect
    // keys off that).
    if (switching) this._messages.set([]);
    this._loadingThread.set(true);
    try {
      const res = await this.conv.getMessages(id);
      const loaded = (res?.messages ?? []) as SupportMessage[];
      this._messages.set(this.toOptimistic(loaded));
      this.lastMessageIso = loaded.length
        ? loaded[loaded.length - 1].created_at
        : null;
      await this.conv.markAsRead(id).catch(() => undefined);
      this._tickets.update((ts) =>
        ts.map((t) => (t.id === id ? { ...t, unread_count: 0 } : t)),
      );
    } catch {
      this._messages.set([]);
      this.lastMessageIso = null;
    } finally {
      this._loadingThread.set(false);
    }
  }

  closeThread(): void {
    this._activeTicketId.set(null);
    this._messages.set([]);
    this.lastMessageIso = null;
    this.evaluatePolling(); // → list (panel open) or background
  }

  startNewConversation(): void {
    this.forceNewTicket = true;
    this._activeTicketId.set(null);
    this._messages.set([]);
    this.lastMessageIso = null;
    // Composing a new ticket is a "thread" for polling purposes even though
    // activeTicketId is still null — evaluatePolling() keys off panelOpen +
    // activeTicketId, so force thread cadence explicitly here.
    if (this._available() && this.lifecycleActive) {
      this._pollMode.set('thread');
      this.rearmPolling();
    }
  }

  async sendMessage(text: string): Promise<void> {
    const body = text.trim();
    if (!body || this._sending() || !this.conv) return;

    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimistic: OptimisticMessage = {
      id: localId,
      localId,
      content: body,
      author_type: 'customer',
      created_at: new Date().toISOString(),
      is_private: false,
      optimisticStatus: 'pending',
    };
    this._messages.update((m) => [...m, optimistic]);
    this._sending.set(true);

    const isNew = this.forceNewTicket || !this._activeTicketId();
    try {
      const res = await this.conv.sendMessage(body, undefined, isNew);
      this.forceNewTicket = false;
      this._messages.update((m) =>
        m.map((msg) =>
          msg.localId === localId
            ? {
                ...msg,
                id: res?.message_id ?? msg.id,
                optimisticStatus: 'sent' as const,
              }
            : msg,
        ),
      );
      if (isNew && res?.ticket_id) {
        this._activeTicketId.set(res.ticket_id);
        await this.refreshTickets();
      }
      this.clearDraft(this._activeTicketId() ?? 'new');
    } catch {
      this._messages.update((m) =>
        m.map((msg) =>
          msg.localId === localId
            ? { ...msg, optimisticStatus: 'failed' as const }
            : msg,
        ),
      );
    } finally {
      this._sending.set(false);
    }
  }

  async retryMessage(localId: string): Promise<void> {
    const msg = this._messages().find((m) => m.localId === localId);
    if (!msg) return;
    this._messages.update((m) => m.filter((x) => x.localId !== localId));
    await this.sendMessage(msg.content);
  }

  // ---- panel <-> poll mode ----

  setPanelOpen(open: boolean): void {
    this.panelOpen = open;
    this.evaluatePolling();
  }

  // ---- polling engine ----

  private intervalFor(mode: PollMode): number | null {
    if (typeof document !== 'undefined' && document.hidden) return null;
    if (this.consecutiveFailures >= 3) return 60_000; // backoff
    switch (mode) {
      case 'thread':
        return 5_000;
      case 'list':
        return 15_000;
      case 'background':
        return 60_000;
      case 'paused':
        return null;
    }
  }

  private clearPollTimer(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private rearmPolling(): void {
    this.clearPollTimer();
    const delay = this.intervalFor(this._pollMode());
    if (delay == null) return;
    this.pollTimer = setTimeout(() => void this.pollOnce(), delay);
  }

  /**
   * Single-flight: only one poll cycle runs at a time. Any trigger that fires
   * while a poll is in flight is dropped (the in-flight poll's `finally`
   * re-arms the next one). This is what prevents concurrent refreshTickets()
   * calls from double-firing the new-message toast.
   */
  private async pollOnce(): Promise<void> {
    if (this.pollInFlight) return;
    this.pollInFlight = true;
    const mode = this._pollMode();
    const ticketId = this._activeTicketId();
    try {
      if (mode === 'thread' && ticketId) {
        await this.pollThread(ticketId);
      } else if (mode === 'list' || mode === 'background') {
        await this.refreshTickets();
      }
      this.consecutiveFailures = 0;
    } catch {
      this.consecutiveFailures++;
    } finally {
      this.pollInFlight = false;
      this.rearmPolling();
    }
  }

  private async pollThread(ticketId: string): Promise<void> {
    if (!this.conv) return;
    const res = await this.conv.getMessages(
      ticketId,
      this.lastMessageIso ?? undefined,
    );
    const incoming: SupportMessage[] = (res?.messages ?? []) as SupportMessage[];
    if (incoming.length === 0) return;
    this._messages.update((current) => {
      const known = new Set(current.map((m) => m.id));
      const additions = incoming
        .filter((m) => !m.is_private && !known.has(m.id))
        .map((m) => ({
          ...m,
          localId: m.id,
          optimisticStatus: 'sent' as const,
        }));
      return additions.length ? [...current, ...additions] : current;
    });
    const newest = incoming[incoming.length - 1];
    if (newest) this.lastMessageIso = newest.created_at;
    await this.conv.markAsRead(ticketId).catch(() => undefined);
  }

  // ---- drafts ----

  readDraft(ticketId: string): string {
    try {
      return this.storage.getItem(DRAFT_PREFIX + ticketId) ?? '';
    } catch {
      return '';
    }
  }

  writeDraft(ticketId: string, text: string): void {
    try {
      if (text) this.storage.setItem(DRAFT_PREFIX + ticketId, text);
      else this.storage.removeItem(DRAFT_PREFIX + ticketId);
    } catch {
      /* private window */
    }
  }

  private clearDraft(ticketId: string): void {
    this.writeDraft(ticketId, '');
  }

  // ---- toast dedup ----

  /**
   * Fire newInboundMessage$ at most once per ticket per "unread episode".
   *
   * A ticket is in the notified set from the moment we toast for it until its
   * unread_count drops back to 0 (user read it). Keying on the ticket id alone
   * — NOT on last_message_at — is deliberate: two near-simultaneous getTickets()
   * responses can carry marginally different last_message_at values for the same
   * message, which produced two dedup keys and two toasts for one reply.
   */
  private detectNewInbound(tickets: SupportTicket[]): void {
    if (this._pollMode() !== 'background') return;
    for (const t of tickets) {
      const unread = t.unread_count ?? 0;
      if (unread === 0) {
        // read elsewhere / by us — allow a future toast for this ticket
        if (this.notifiedMessageIds.delete(t.id)) this.writeNotifiedIds();
        continue;
      }
      if (this.notifiedMessageIds.has(t.id)) continue;
      this.notifiedMessageIds.add(t.id);
      this.writeNotifiedIds();
      this.newInboundMessage$.next({
        id: t.id,
        content: t.last_message ?? '',
        author_type: 'human',
        created_at: t.last_message_at ?? new Date().toISOString(),
        is_private: false,
      });
    }
  }

  private readNotifiedIds(): Set<string> {
    try {
      const raw = sessionStorage.getItem(NOTIFIED_KEY);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
      return new Set();
    }
  }

  private writeNotifiedIds(): void {
    try {
      sessionStorage.setItem(
        NOTIFIED_KEY,
        JSON.stringify([...this.notifiedMessageIds]),
      );
    } catch {
      /* private window */
    }
  }

  // ---- helpers ----

  private toOptimistic(msgs: SupportMessage[]): OptimisticMessage[] {
    return msgs
      .filter((m) => !m.is_private)
      .map((m) => ({
        ...m,
        localId: m.id,
        optimisticStatus: 'sent' as const,
      }));
  }
}
